import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, scan} from 'rxjs';
import {downloadResponseAsFile, mapParseItem, SseFrame, sseFrames} from '@brunata-angular/util';
import {environment} from 'src/environments/environment';
import {
    SftpConnection,
    SftpConnectionPage,
    SftpConnectionRequest,
    SftpConnectionTestProgress,
    SftpConnectionTestRequest,
    sftpConnectionPageSchema,
    sftpConnectionSchema,
    sftpConnectionTestResultSchema,
    sftpConnectionTestStageSchema,
    SftpTestServerFile,
    SftpTestServerFiles,
    sftpTestServerFilesSchema,
} from './sftp-connection.model';

const startedTest: SftpConnectionTestProgress = {state: 'running', details: {}};

const applyTestFrame = (
    progress: SftpConnectionTestProgress,
    frame: SseFrame,
): SftpConnectionTestProgress => {
    if (frame.event === 'result') {
        const result = sftpConnectionTestResultSchema.parse(JSON.parse(frame.data));
        return {...progress, state: result.succeeded ? 'succeeded' : 'failed'};
    }
    const stage = sftpConnectionTestStageSchema.safeParse(frame.event);
    if (!stage.success) {
        return progress;
    }
    const details = {...progress.details};
    details[stage.data] = frame.data;
    return {state: 'running', details};
};

@Injectable({
    providedIn: 'root',
})
export class SftpConnectionsService {
    private readonly baseUrl = environment.onlineServiceUrl;
    private readonly http = inject(HttpClient);

    getSftpConnections(): Observable<SftpConnectionPage> {
        return this.http
            .get(`${this.baseUrl}/sftpconnections`, {withCredentials: true})
            .pipe(mapParseItem(sftpConnectionPageSchema));
    }

    createSftpConnection(payload: SftpConnectionRequest): Observable<SftpConnection> {
        return this.http
            .post(`${this.baseUrl}/sftpconnections`, payload, {withCredentials: true})
            .pipe(mapParseItem(sftpConnectionSchema));
    }

    updateSftpConnection(
        sftpConnectionId: string,
        payload: SftpConnectionRequest,
    ): Observable<SftpConnection> {
        return this.http
            .put(`${this.baseUrl}/sftpconnections/${sftpConnectionId}`, payload, {
                withCredentials: true,
            })
            .pipe(mapParseItem(sftpConnectionSchema));
    }

    deleteSftpConnection(sftpConnectionId: string): Observable<void> {
        return this.http.delete<void>(`${this.baseUrl}/sftpconnections/${sftpConnectionId}`, {
            withCredentials: true,
        });
    }

    testSftpConnection(payload: SftpConnectionTestRequest): Observable<SftpConnectionTestProgress> {
        return this.http
            .post(`${this.baseUrl}/sftpconnections/test`, payload, {
                withCredentials: true,
                observe: 'events',
                reportProgress: true,
                responseType: 'text',
                headers: {accept: 'text/event-stream'},
            })
            .pipe(sseFrames(), scan(applyTestFrame, startedTest));
    }

    getSftpTestServerFiles(sftpConnectionId: string): Observable<SftpTestServerFiles> {
        return this.http
            .get(`${this.baseUrl}/sftpconnections/${sftpConnectionId}/testserverfiles`, {
                withCredentials: true,
            })
            .pipe(mapParseItem(sftpTestServerFilesSchema));
    }

    downloadSftpTestServerFile(
        sftpConnectionId: string,
        file: SftpTestServerFile,
    ): Observable<unknown> {
        return this.http
            .get(`${this.baseUrl}/sftpconnections/${sftpConnectionId}/testserverfiles/download`, {
                params: {folder: file.folder, fileName: file.fileName},
                observe: 'response',
                responseType: 'blob',
                withCredentials: true,
            })
            .pipe(downloadResponseAsFile(() => file.fileName));
    }
}
